import React, { useState, useEffect } from "react";

const DateDifferenceCalculator = () => {
  const [startYear, setStartYear] = useState(2020);
  const [startMonth, setStartMonth] = useState(1);
  const [startDay, setStartDay] = useState(1);
  const [endYear, setEndYear] = useState(2024);
  const [endMonth, setEndMonth] = useState(11);
  const [endDay, setEndDay] = useState(3);
  const [calculationType, setCalculationType] = useState("duration");
  const [roundUp, setRoundUp] = useState(false);
  const [includeEndDate, setIncludeEndDate] = useState(false);
  const [result, setResult] = useState({
    years: "0",
    months: "0",
    days: "0",
    totalDays: "0",
    totalWeeks: "0",
    totalMonths: "0",
    totalHours: "0",
    calculationTypeText: "",
    differenceTip: "",
  });

  // Date Difference Calculation Function
  const calculateDateDifference = () => {
    let sYear = parseInt(startYear);
    let sMonth = parseInt(startMonth);
    let sDay = parseInt(startDay);
    let eYear = parseInt(endYear);
    let eMonth = parseInt(endMonth);
    let eDay = parseInt(endDay);

    if (isNaN(sYear) || isNaN(sMonth) || isNaN(sDay) || isNaN(eYear) || isNaN(eMonth) || isNaN(eDay)) return;

    // Create date objects
    const startDate = new Date(sYear, sMonth - 1, sDay);
    const endDate = new Date(eYear, eMonth - 1, eDay);

    // Ensure start date is before end date
    let fromDate = startDate;
    let toDate = endDate;
    if (startDate > endDate) {
      fromDate = endDate;
      toDate = startDate;
    }

    // Calculate difference
    let years = toDate.getFullYear() - fromDate.getFullYear();
    let months = toDate.getMonth() - fromDate.getMonth();
    let days = toDate.getDate() - fromDate.getDate();

    // Adjust for negative days
    if (days < 0) {
      months--;
      const prevMonth = new Date(toDate.getFullYear(), toDate.getMonth(), 0);
      days += prevMonth.getDate();
    }

    // Adjust for negative months
    if (months < 0) {
      years--;
      months += 12;
    }

    // Round up if selected
    if (roundUp && days > 15) {
      months++;
      days = 0;
      if (months >= 12) {
        years++;
        months = 0;
      }
    }

    // Calculate total values
    let timeDiff = toDate - fromDate;
    if (includeEndDate) {
      timeDiff += (1000 * 60 * 60 * 24); // Add one day
    }
    
    const totalDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
    const totalWeeks = Math.floor(totalDays / 7);
    const totalMonths = years * 12 + months;
    const totalHours = totalDays * 24;

    let typeText = "";
    let differenceTip = "";

    switch(calculationType) {
      case "duration":
        typeText = "Duration Between Dates";
        differenceTip = "Perfect for calculating project timelines, vacation lengths, and event planning durations.";
        break;
      case "workdays":
        typeText = "Work Days Calculation";
        differenceTip = "Useful for calculating working days excluding weekends. Ideal for project management and payroll.";
        break;
      case "business":
        typeText = "Business Date Range";
        differenceTip = "Calculate business periods for invoicing, contracts, and subscription periods.";
        break;
      case "anniversary":
        typeText = "Anniversary Calculator";
        differenceTip = "Track time since important events like weddings, company founding, or relationship milestones.";
        break;
      case "custom":
        typeText = "Custom Date Range";
        differenceTip = "Calculate difference between any two dates for custom needs and analysis.";
        break;
      default:
        typeText = "Date Difference";
        differenceTip = "Understanding time between dates helps with planning and time management.";
    }

    setResult({
      years: years.toString(),
      months: months.toString(),
      days: days.toString(),
      totalDays: totalDays.toLocaleString("en-IN"),
      totalWeeks: totalWeeks.toLocaleString("en-IN"),
      totalMonths: totalMonths.toLocaleString("en-IN"),
      totalHours: totalHours.toLocaleString("en-IN"),
      calculationTypeText: typeText,
      differenceTip,
    });
  };

  useEffect(() => {
  calculateDateDifference();
}, [
  startYear,
  startMonth,
  startDay,
  endYear,
  endMonth,
  endDay,
  calculationType,
  roundUp,
  includeEndDate
]);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <h1>Date Difference Calculator</h1>
          <p>
            Calculate time between two dates in years, months, days, weeks, and hours accurately
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                  <line x1="16" y1="2" x2="16" y2="6"></line>
                  <line x1="8" y1="2" x2="8" y2="6"></line>
                  <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
                Date Range
              </h2>

              {/* Start Year */}
              <div className="input-group">
                <label>Start Year</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📅</span>
                  <input
                    type="number"
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    className="calc-input"
                    placeholder="2020"
                    min="1900"
                    max="2100"
                  />
                </div>
                <div className="quick-select-group">
                  {[2020, 2021, 2022, 2023].map((year) => (
                    <button
                      key={year}
                      onClick={() => setStartYear(year)}
                      className="quick-select-btn primary"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* Start Month */}
              <div className="input-group">
                <label>Start Month</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📆</span>
                  <input
                    type="number"
                    value={startMonth}
                    onChange={(e) => setStartMonth(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                    max="12"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 4, 7, 10].map((month) => (
                    <button
                      key={month}
                      onClick={() => setStartMonth(month)}
                      className="quick-select-btn success"
                    >
                      {["Jan", "Apr", "Jul", "Oct"][month === 1 ? 0 : month === 4 ? 1 : month === 7 ? 2 : 3]}
                    </button>
                  ))}
                </div>
              </div>

              {/* Start Day */}
              <div className="input-group">
                <label>Start Day</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📍</span>
                  <input
                    type="number"
                    value={startDay}
                    onChange={(e) => setStartDay(e.target.value)}
                    className="calc-input"
                    placeholder="1"
                    min="1"
                    max="31"
                  />
                </div>
                <div className="quick-select-group">
                  {[1, 10, 20, 30].map((day) => (
                    <button
                      key={day}
                      onClick={() => setStartDay(day)}
                      className="quick-select-btn success"
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculation Type */}
              <div className="input-group">
                <label>Calculation Type</label>
                <div className="quick-select-group">
                  {[
                    { value: "duration", label: "Duration" },
                    { value: "business", label: "Business Days" },
                    { value: "anniversary", label: "Anniversary" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
                <div className="quick-select-group" style={{marginTop: '0.5rem'}}>
                  {[
                    { value: "workdays", label: "Work Days" },
                    { value: "custom", label: "Custom" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setCalculationType(type.value)}
                      className={`quick-select-btn ${calculationType === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* End Year */}
              <div className="input-group">
                <label>End Year</label>
                <div className="input-wrapper">
                  <span className="input-prefix">🎯</span>
                  <input
                    type="number"
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    className="calc-input"
                    placeholder="2024"
                    min="1900"
                    max="2100"
                  />
                </div>
                <div className="quick-select-group">
                  {[2023, 2024, 2025, 2026].map((year) => (
                    <button
                      key={year}
                      onClick={() => setEndYear(year)}
                      className="quick-select-btn success"
                    >
                      {year}
                    </button>
                  ))}
                </div>
              </div>

              {/* End Month */}
              <div className="input-group">
                <label>End Month</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📆</span>
                  <input
                    type="number"
                    value={endMonth}
                    onChange={(e) => setEndMonth(e.target.value)}
                    className="calc-input"
                    placeholder="11"
                    min="1"
                    max="12"
                  />
                </div>
                <div className="quick-select-group">
                  {[3, 6, 9, 12].map((month) => (
                    <button
                      key={month}
                      onClick={() => setEndMonth(month)}
                      className="quick-select-btn success"
                    >
                      {["Mar", "Jun", "Sep", "Dec"][month === 3 ? 0 : month === 6 ? 1 : month === 9 ? 2 : 3]}
                    </button>
                  ))}
                </div>
              </div>

              {/* End Day */}
              <div className="input-group">
                <label>End Day</label>
                <div className="input-wrapper">
                  <span className="input-prefix">📍</span>
                  <input
                    type="number"
                    value={endDay}
                    onChange={(e) => setEndDay(e.target.value)}
                    className="calc-input"
                    placeholder="3"
                    min="1"
                    max="31"
                  />
                </div>
                <div className="quick-select-group">
                  {[5, 15, 25, 31].map((day) => (
                    <button
                      key={day}
                      onClick={() => setEndDay(day)}
                      className="quick-select-btn success"
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Include End Date */}
              <div className="input-group">
                <label>Include End Date?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Include)" },
                    { value: false, label: "No (Exclude)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setIncludeEndDate(type.value)}
                      className={`quick-select-btn ${includeEndDate === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Round Up */}
              <div className="input-group">
                <label>Round Months?</label>
                <div className="quick-select-group">
                  {[
                    { value: true, label: "Yes (Round Up)" },
                    { value: false, label: "No (Exact)" }
                  ].map((type) => (
                    <button
                      key={type.value.toString()}
                      onClick={() => setRoundUp(type.value)}
                      className={`quick-select-btn ${roundUp === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              <button onClick={calculateDateDifference} className="calculate-btn">
                Calculate Difference
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Time Difference
              </h2>

              <div className="result-card">
                {/* Duration - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Duration</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.years} Years
                  </div>
                  <div className="result-subtext">
                    {result.months} Months, {result.days} Days
                  </div>
                </div>

                {/* Total Days */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Days</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalDays}
                  </div>
                  <div className="result-subtext">
                    Days between dates
                  </div>
                </div>

                {/* Total Weeks */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Weeks</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalWeeks}
                  </div>
                  <div className="result-subtext">
                    Weeks in duration
                  </div>
                </div>

                {/* Total Months */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Months</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalMonths}
                  </div>
                  <div className="result-subtext">
                    Months in duration
                  </div>
                </div>

                {/* Total Hours */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Total Hours</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    {result.totalHours}
                  </div>
                  <div className="result-subtext">
                    Hours in duration
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Duration Breakdown</h3>
                  
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Years Component</span>
                      <span className="breakdown-percentage">
                        {result.years} Years
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseInt(result.totalMonths) > 0 ? ((parseInt(result.years) * 12) / parseInt(result.totalMonths)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Remaining Months</span>
                      <span className="breakdown-percentage">
                        {result.months} Months
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${parseInt(result.totalMonths) > 0 ? (parseInt(result.months) / parseInt(result.totalMonths)) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Time Units Distribution */}
                <div className="breakdown-section" style={{marginTop: '1.5rem'}}>
                  <h3 className="breakdown-title">Time Units Comparison</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">In Weeks</span>
                      <span className="breakdown-percentage">
                        {result.totalWeeks} Weeks
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${parseInt(result.totalDays.replace(/,/g, '')) > 0 ? (parseInt(result.totalWeeks.replace(/,/g, '')) * 7 / parseInt(result.totalDays.replace(/,/g, ''))) * 100 : 0}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">In Days</span>
                      <span className="breakdown-percentage">
                        {result.totalDays} Days
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `100%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Date Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Date Insights</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.differenceTip} Accurate date calculations are essential for planning.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Date Differences</h3>
              <p>
                Date difference calculation determines the duration between two dates, expressed in various units like 
                years, months, days, weeks, and hours. This is essential for project management, contract durations, 
                event planning, and timeline analysis. Accurate date calculations account for varying month lengths 
                (28-31 days) and leap years (February 29 in leap years). When calculating business days, weekends are 
                typically excluded, and some calculations also exclude holidays. The choice to include or exclude the 
                end date affects the count by one day - useful for rental periods, subscription billing, and event 
                planning. Date differences are used in various contexts: calculating project timelines (start to 
                completion), tracking relationship milestones (anniversaries), determining loan periods (EMI calculations), 
                measuring employee tenure (work experience), and analyzing historical events (time since occurrence). 
                Working days calculations are crucial for payroll, project deadlines, and service level agreements (SLAs). 
                Understanding date arithmetic helps avoid common mistakes like off-by-one errors. Calendar applications 
                use date difference calculations for reminders, countdowns, and scheduling. Financial applications use 
                them for interest calculations, investment periods, and maturity dates.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Date Difference Calculation:</h4>
            <div className="formula-display">
              Difference = End Date - Start Date (in various units)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Total Days</div>
                <div className="formula-param-value">Difference in milliseconds / (1000 × 60 × 60 × 24)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Weeks</div>
                <div className="formula-param-value">Total Days / 7</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Years, Months, Days</div>
                <div className="formula-param-value">Calculate years first, then remaining months, then days</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Total Hours</div>
                <div className="formula-param-value">Total Days × 24</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Should I include the end date in my calculation?</h5>
              <p>
                It depends on your use case. For rental periods and hotel stays, the end date is usually excluded (checkout day doesn't count). For event durations and project timelines, it's often included.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do leap years affect date calculations?</h5>
              <p>
                Leap years add an extra day (February 29) every four years. This affects calculations spanning February in leap years. The calculator automatically accounts for leap years in its calculations.
              </p>
            </div>

            <div className="faq-item">
              <h5>What's the difference between calendar days and business days?</h5>
              <p>
                Calendar days include all days (Monday-Sunday). Business days typically exclude weekends (Saturday-Sunday) and sometimes holidays. A 10 calendar day period might only be 6-7 business days.
              </p>
            </div>

            <div className="faq-item">
              <h5>How do I calculate working days between two dates?</h5>
              <p>
                Count total days, then subtract weekends. Formula: Total Days - (Number of Weeks × 2). For precise calculations, account for partial weeks and specific holidays in your region.
              </p>
            </div>

            <div className="faq-item">
              <h5>Why do months have different calculations than days?</h5>
              <p>
                Months vary in length (28-31 days), making month calculations more complex. The calculator counts complete months first, then adds remaining days for accuracy.
              </p>
            </div>

            <div className="faq-item">
              <h5>How accurate are date difference calculations?</h5>
              <p>
                Very accurate for standard calendar calculations. However, for financial calculations with specific day count conventions (30/360, actual/360), specialized formulas may be needed.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DateDifferenceCalculator;